'use client';

import { useMemo, useState } from 'react';
import dynamic from 'next/dynamic';

import { useBoolean } from 'codekit';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Tooltip } from 'next-bricks';

import Button from 'components/Button';
import { Icon } from 'components/Icon';
import RenderOn from 'components/RenderOn';
import { DEMO_BASE_CODE, DEMO_MAX_MESSAGES } from 'constants/demo';
import { useFetch } from 'hooks/useFetch';
import { useVisitorId } from 'hooks/useVisitorId';
import { DemoMessage } from 'models/DemoMessage';
import { api } from 'services/api';
import { cn } from 'utils/cn';
import Section from '..';
import Tag from '../Elements/Tag';
import Text from '../Elements/Text';
import Message from './Message';

import styles from './styles.module.css';

const EditorMonaco = dynamic(() => import('./Editor'), {
	ssr: false,
});

function SectionDemo() {
	const { data: visitorId } = useVisitorId();

	const [base, setBase] = useState<string>(DEMO_BASE_CODE);
	const [code, setCode] = useState<string | null>(DEMO_BASE_CODE);

	const isLoadingEditor = useBoolean(true);
	const isGenerating = useBoolean(false);

	const messages = useFetch<DemoMessage[]>(visitorId && `/demo/${visitorId}`);

	const filteredMessages = useMemo(() => {
		return (messages.data ?? []).order('createdAt', 'asc');
	}, [messages.data]);

	const usage = useMemo(() => {
		const max = DEMO_MAX_MESSAGES;
		const generated = filteredMessages.length;
		const percentage = (generated / max) * 100;

		return {
			max,
			generated,
			percentage,
		};
	}, [filteredMessages]);

	async function handleGenerate() {
		if (!code) {
			return toast.error('Você não fez nenhuma alteração no código');
		}

		isGenerating.setTrue();

		await api.post(`/demo/${visitorId}`, {
			initialCode: base,
			changedCode: code,
		});

		setBase(code);
		await messages.mutate();

		isGenerating.setFalse();
	}

	return (
		<Section className={styles.container}>
			<Text align="center" heading>
				<Tag>Demo</Tag>

				<h2>
					Experimente seus <span>git</span> commits <br /> com{' '}
					<span>super</span>
					poderes
				</h2>
				<p>
					Faça alterações no código e veja como os commits são gerados
					a partir das suas alterações
				</p>
			</Text>

			<div className={styles.demo}>
				<div className={styles.code}>
					<RenderOn.Idle>
						<RenderOn.InView>
							<EditorMonaco
								code={code}
								onChangeAction={setCode}
								onMount={isLoadingEditor.setFalse}
							/>
						</RenderOn.InView>
					</RenderOn.Idle>
				</div>

				<div className={styles.commit}>
					<div className={cn(styles.block, styles.preview)}>
						<div className={styles.preview__heading}>
							<div className={styles.usage}>
								<div className={styles.usage__progress}>
									<motion.div
										className={
											styles.usage__progress__state
										}
										animate={{
											width: usage.percentage + '%',
										}}
									/>
								</div>

								<span>
									{usage.generated} / {usage.max} Commits
									gerados
								</span>
							</div>

							<Tooltip
								label={
									base === code
										? 'Você precisa fazer alterações no código para gerar um commit'
										: usage.generated === usage.max
											? 'Você atingiu o limite de commits gerados'
											: ''
								}
								disabled={
									base !== code && usage.generated < usage.max
								}
							>
								<Button
									onClick={handleGenerate}
									loading={isGenerating.value}
									disabled={
										base === code ||
										usage.generated === usage.max
									}
								>
									<Icon name="Bot" size={18} />
									Gerar commit
								</Button>
							</Tooltip>
						</div>

						<div className={styles.preview__content}>
							{filteredMessages.length === 0 && (
								<p className="no-data">
									Nenhum commit foi gerado ainda
								</p>
							)}

							{filteredMessages.length > 0 && (
								<div className={styles.messages}>
									{filteredMessages.map(
										(message, index, array) => (
											<Message
												key={message.id}
												message={message.message}
												index={index}
												messages={array}
											/>
										),
									)}
								</div>
							)}
						</div>
					</div>

					<Tooltip
						label="Você pode alterar essa opções nas configurações do seu perfil"
						position="bottom"
					>
						<div className={cn(styles.block, styles.config)}>
							<div className={styles.config__item}>
								<small>Idioma</small>
								<strong>Inglês</strong>
							</div>

							<div className={styles.config__item}>
								<small>Usar escopo</small>
								<strong>Sim</strong>
							</div>

							<div className={styles.config__item}>
								<small>Usar git emoji</small>
								<strong>Sim</strong>
							</div>

							<div className={styles.config__item}>
								<small>Padrão</small>
								<strong>Conventional Commits</strong>
							</div>
						</div>
					</Tooltip>
				</div>
			</div>
		</Section>
	);
}

export default SectionDemo;
